"use client";
import { IBlocks, ITheme, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import LoadingSkeleton from "./LoadingSkeleton";
import { IconBrandYoutube } from "@tabler/icons-react";
import AdminPopover from "../../content/AdminPopover";
import { SettingTabs } from "../../content/SettingTabs";
import YoutubeOptions from "./tabs/youtube/options";
import { Label } from "@/components/ui/label";
import BlockLink from "./link";
import { Separator } from "@/components/ui/separator";
import YoutubeCustomization from "./tabs/youtube/customization";
import BlockToggle from "./tabs/toggle";
import BlockMetadata from "./tabs/metadata";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    theme: ITheme;
    style: any;
    newBlock?: string | null;
}
export default function BlockYoutube({
    admin,
    user,
    block,
    update,
    theme,
    style,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [youtube, setYoutube] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(true);
    const [hide, setHide] = React.useState(false);
    React.useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setHide(false);
            try {
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_API_SERVER}/v2/live/youtube/${blockUpdate.url}`,
                    { cache: "no-store" }
                );
                const data = await response.json();
                if (!data.live) {
                    if (blockUpdate.options?.hide_offline) {
                        if (!admin) setHide(true);
                        setLoading(false);
                        setYoutube(null);
                    } else {
                        setLoading(false);
                        setYoutube(null);
                        setHide(true);
                    }
                } else {
                    setYoutube(data);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [blockUpdate, user, admin]);
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "YouTube Livestream",
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <YoutubeOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <YoutubeCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={[]}
                        user={user}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            title={`YouTube - ${blockUpdate.url}`}
            onClick={handleExpand}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            {!hide ? (
                <div className="relative aspect-video w-full min-w-full">
                    <div
                        className={`absolute flex w-full min-w-full place-content-center items-center justify-center transition-opacity duration-300 ${loading ? "opacity-100" : "pointer-events-none opacity-0"}`}
                    >
                        <LoadingSkeleton />
                        <IconBrandYoutube
                            className="absolute z-50 animate-bounce rounded-md bg-red-500 p-1 shadow-xl"
                            size={40}
                        />
                    </div>
                    <div
                        className={`w-full min-w-full transition-opacity duration-300 ${loading ? "opacity-0" : "opacity-100"}`}
                    >
                        {youtube ? (
                            <iframe
                                id="youtube-live-player"
                                className="aspect-video h-full w-full rounded-lg"
                                src={`https://www.youtube.com/embed/${youtube?.url}?enablejsapi=1&mute=true${admin ? "&autoplay=false" : "&autoplay=true"}`}
                                title={youtube?.ogTitle}
                                allow=""
                                onLoad={(e) => {
                                    setLoading(false);
                                }}
                                allowFullScreen
                            />
                        ) : (
                            <div
                                className={`absolute flex w-full min-w-full place-content-center items-center justify-center opacity-100 transition-opacity duration-300`}
                            >
                                <LoadingSkeleton nopulse />
                                <div className="absolute flex flex-col items-center">
                                    <IconBrandYoutube
                                        className="z-50 rounded-md bg-red-500 p-1 shadow-xl"
                                        size={40}
                                    />
                                    <Label className="" variant="headingtoggle">
                                        (Preview)
                                    </Label>
                                    <Label className="" variant="headingtoggle">
                                        Stream will appear here when it's live.
                                    </Label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : !blockUpdate.options?.hide_offline ? (
                <BlockLink
                    user={user}
                    block={{
                        ...blockUpdate,
                        url: "https://www.youtube.com/" + blockUpdate.url,
                    }}
                    update={update}
                    admin={false}
                    style={style}
                    assets={[]}
                    theme={theme}
                ></BlockLink>
            ) : admin ? (
                <div
                    className={`absolute flex w-full min-w-full place-content-center items-center justify-center opacity-100 transition-opacity duration-300`}
                >
                    <LoadingSkeleton nopulse />
                    <div className="absolute flex flex-col items-center">
                        <IconBrandYoutube
                            className="z-50 rounded-md bg-red-500 p-1 shadow-xl"
                            size={40}
                        />
                        <Label className="" variant="headingtoggle">
                            (Preview)
                        </Label>
                        <Label className="" variant="headingtoggle">
                            Stream will appear here when it's live.
                        </Label>
                    </div>
                </div>
            ) : (
                HideElementsByClass(block.id)
            )}
        </AdminPopover>
    );
}
function HideElementsByClass(block_id: string) {
    if (
        window.document &&
        window.document.getElementsByClassName("block-" + block_id)[0]
    ) {
        // Get all elements with the class name 'block-' + block_id
        const elements = document.getElementsByClassName("block-" + block_id);
        // Loop through each element and add the 'hidden' attribute
        for (var i = 0; i < elements.length; i++) {
            elements[i].classList.add("hidden");
        }
        return null;
    }
    return null;
}
